import React from 'react';
import Grid2 from '@mui/material/Grid2';
import { FormControlLabel, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import TextField from '../../../components/TextField';
import AddressAutocomplete from '../../../components/AddressAutocomplete';
import Picklist from '../../../components/Picklist';
import MultiSelectPicklist from '../../../components/MultiSelectPicklist';
import FormSection from '../../../components/FormSection';
import { CaregiverFormData } from '../types';
import { FieldErrors } from '../validation';
import { US_STATES } from '../constants';

interface Props {
  formData: CaregiverFormData;
  fieldErrors: FieldErrors;
  handleChange: (e: { target: { name: string; value: any } }) => void;
  handleCheckboxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMultiSelectChange: (e: { target: { name: string; value: string[] } }) => void;
  setFormData: React.Dispatch<React.SetStateAction<CaregiverFormData>>;
}

const DemographicsStep: React.FC<Props> = ({
  formData,
  fieldErrors,
  handleChange,
  handleCheckboxChange,
  handleMultiSelectChange,
  setFormData,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <FormSection title={t('form.basicInfo', 'Basic Information')} icon={<PersonIcon />}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              required
              name="firstName"
              label={t('form.firstName', 'First Name')}
              value={formData.firstName}
              onChange={handleChange}
              error={!!fieldErrors.firstName}
              helperText={fieldErrors.firstName || t('form.mustMatchId', 'Must match your ID')}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              name="middleName"
              label={t('form.middleName', 'Middle Name')}
              value={formData.middleName}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              required
              name="lastName"
              label={t('form.lastName', 'Last Name')}
              value={formData.lastName}
              onChange={handleChange}
              error={!!fieldErrors.lastName}
              helperText={fieldErrors.lastName}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              name="otherLastName"
              label={t('form.otherLastName', 'Other Last Name')}
              value={formData.otherLastName}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Picklist
              fieldName="gender"
              value={formData.gender}
              onChange={handleChange}
              label={t('form.gender', 'Gender')}
              objectName="caregiver"
              required
              error={!!fieldErrors.gender}
              helperText={fieldErrors.gender}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="dateOfBirth"
              label={t('form.dateOfBirth', 'Date of Birth')}
              type="date"
              value={formData.dateOfBirth}
              onChange={handleChange}
              error={!!fieldErrors.dateOfBirth}
              helperText={fieldErrors.dateOfBirth}
              InputLabelProps={{ shrink: true }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="email"
              label={t('form.email', 'Email')}
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              name="phone"
              label={t('form.phone', 'Phone')}
              value={formData.phone}
              onChange={handleChange}
              inputMask="999 999 9999"
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Picklist
              fieldName="primaryLanguage"
              value={formData.primaryLanguage}
              onChange={handleChange}
              label={t('form.primaryLanguage', 'Primary Language')}
              objectName="caregiver"
              required
              error={!!fieldErrors.primaryLanguage}
              helperText={fieldErrors.primaryLanguage}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <MultiSelectPicklist
              fieldName="additionalLanguages"
              value={formData.additionalLanguages}
              onChange={handleMultiSelectChange}
              label={t('form.additionalLanguages', 'Additional Languages')}
              objectName="caregiver"
              maxSelections={3}
            />
          </Grid2>
          <Grid2 size={12}>
            <TextField
              fullWidth
              required
              name="howDidYouHearAboutUs"
              label={t('form.howDidYouHearAboutUs', 'How did you hear about us?')}
              value={formData.howDidYouHearAboutUs}
              onChange={handleChange}
              error={!!fieldErrors.howDidYouHearAboutUs}
              helperText={fieldErrors.howDidYouHearAboutUs}
            />
          </Grid2>
        </Grid2>
      </FormSection>

      <FormSection title={t('form.mailingAddress', 'Mailing Address')} icon={<HomeIcon />}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <AddressAutocomplete
              label={t('form.searchMailingAddress', 'Search Mailing Address')}
              onAddressSelect={({ street, aptSuite, city, state, zipCode }) =>
                setFormData((prev) => ({
                  ...prev,
                  street,
                  aptUnitNumber: aptSuite,
                  city,
                  state,
                  zipCode,
                }))
              }
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="street"
              label={t('form.street', 'Street Address')}
              value={formData.street}
              onChange={handleChange}
              error={!!fieldErrors.street}
              helperText={fieldErrors.street}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              name="aptUnitNumber"
              label={t('form.aptUnitNumber', 'Apartment Number')}
              value={formData.aptUnitNumber}
              onChange={handleChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="city"
              label={t('form.city', 'City')}
              value={formData.city}
              onChange={handleChange}
              error={!!fieldErrors.city}
              helperText={fieldErrors.city}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Picklist
              fieldName="state"
              value={formData.state}
              onChange={handleChange}
              label={t('form.state', 'State')}
              required
              error={!!fieldErrors.state}
              helperText={fieldErrors.state}
              options={US_STATES}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              name="zipCode"
              label={t('form.zipCode', 'Zip Code')}
              value={formData.zipCode}
              onChange={handleChange}
              inputMask="99999"
              error={!!fieldErrors.zipCode}
              helperText={fieldErrors.zipCode}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Picklist
              fieldName="countyName"
              value={formData.countyName}
              onChange={handleChange}
              label={t('form.countyName', 'County')}
              objectName="caregiver"
              required
              error={!!fieldErrors.countyName}
              helperText={fieldErrors.countyName}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.physicalAddressDifferentThanMailing}
                  onChange={handleCheckboxChange}
                  name="physicalAddressDifferentThanMailing"
                />
              }
              label={t('form.physicalAddressDifferent', 'Physical address is different than mailing address')}
            />
          </Grid2>
        </Grid2>
      </FormSection>

      {formData.physicalAddressDifferentThanMailing && (
        <FormSection title={t('form.physicalAddress', 'Physical Address')} icon={<HomeIcon />}>
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <AddressAutocomplete
                label={t('form.searchPhysicalAddress', 'Search Physical Address')}
                onAddressSelect={({ street, aptSuite, city, state, zipCode }) =>
                  setFormData((prev) => ({
                    ...prev,
                    otherAddressLine1: street,
                    otherAptUnitNumber: aptSuite,
                    otherCity: city,
                    otherState: state,
                    otherZipCode: zipCode,
                  }))
                }
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                name="otherAddressLine1"
              label={t('form.street', 'Street Address')}
              value={formData.otherAddressLine1}
                onChange={handleChange}
                error={!!fieldErrors.otherAddressLine1}
                helperText={fieldErrors.otherAddressLine1}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                name="otherAptUnitNumber"
              label={t('form.aptUnitNumber', 'Apartment Number')}
              value={formData.otherAptUnitNumber}
                onChange={handleChange}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                name="otherCity"
                label={t('form.city', 'City')}
                value={formData.otherCity}
                onChange={handleChange}
                error={!!fieldErrors.otherCity}
                helperText={fieldErrors.otherCity}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <Picklist
                fieldName="otherState"
                value={formData.otherState}
                onChange={handleChange}
                label={t('form.state', 'State')}
                required
                error={!!fieldErrors.otherState}
                helperText={fieldErrors.otherState}
                options={US_STATES}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                required
                name="otherZipCode"
                label={t('form.zipCode', 'Zip Code')}
                value={formData.otherZipCode}
                onChange={handleChange}
                inputMask="99999"
                error={!!fieldErrors.otherZipCode}
                helperText={fieldErrors.otherZipCode}
              />
            </Grid2>
          </Grid2>
        </FormSection>
      )}
    </>
  );
};

export default DemographicsStep;
